import React, { useRef, useEffect, useState, useCallback } from 'react';
import SignaturePad from 'signature_pad';
import styled from 'styled-components';
import Button from '../button';
import InlineLoading from '../inline-loading';
import { QRCode } from 'react-qrcode-logo';
import { FormattedMessage } from 'react-intl';
import { areaApi } from '../../../utils/api';

const SignatureModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 32px 32px 32px;
  background: #fff;
  border-radius: 12px;
  min-width: 340px;
  max-width: 520px; /* allow a bit wider modal to fit aspect box better */
  width: 100%;
  box-sizing: border-box;
  position: relative;
  @media (max-width: 600px) {
    min-width: 0;
    max-width: 100vw;
    padding: 24px 8px 16px 8px;
  }

  .close-button {
    min-width: 0 !important;
    position: absolute;
    top: 16px;
    right: 16px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    z-index: 2;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .signature-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 12px; /* reduced to make space for hint */
    text-align: center;
    width: 100%;
  }
  .signature-hint {
    font-size: 1rem; /* compact */
    line-height: 1.15; /* tighter */
    color: #555;
    text-align: center;
    margin: 0 0 12px 0; /* space below */
    max-width: 480px;
  }
  .signature-pad {
    border: 1.5px solid #e0e0e0;
    border-radius: 12px; /* match button radius */
    background: #fff;
    width: 100%;
    max-width: 480px; /* wider canvas box */
    aspect-ratio: 18 / 6; /* enforce 18:6 visual box */
    height: auto; /* let aspect-ratio define height */
    margin-bottom: 18px;
    display: block;
    @media (max-width: 600px) {
      width: 100%;
      min-width: 0;
      max-width: 100%;
    }
  }
  .canvas-wrap {
    position: relative;
    width: 100%;
    max-width: 480px;
  }
  .clear-icon {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1px solid #e0e0e0;
    background: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(0,0,0,0.06);
    z-index: 1;
  }
  .clear-icon:disabled {
    opacity: 0.6;
    cursor: default;
  }
  .clear-icon svg {
    width: 24px;
    height: 24px;
    fill: #6b7280; /* gray-500 */
  }
  .clear-icon:hover svg {
    fill: #374151; /* gray-700 */
  }
  .signature-actions {
    display: flex;
    gap: 12px;
    margin-bottom: 8px;
    width: 100%;
    justify-content: center;
    flex-wrap: nowrap; /* keep in one row */
    align-items: center;
  }
  
  /* Normalize action buttons appearance */
  .signature-actions > button {
    text-transform: none !important;
    min-height: 44px;
    padding: 10px 16px;
  }
  .signature-actions > button * {
    text-transform: none !important;
  }

  .qr-panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 12px 8px 0 8px;
    width: 100%;
    box-sizing: border-box;
    text-align: center;
  }
  .qr-instruction {
    font-size: 14px;
    color: #555;
    margin: 12px 0 4px 0;
  }
  .qr-url {
    font-size: 12px;
    color: #888;
    word-break: break-all;
    max-width: 100%;
  }
`;


const SignatureModal = ({ isOpened, onClose, onSubmit, mobileSignUrl, preLoanId }) => {
  const canvasRef = useRef();
  const sigPadRef = useRef();
  const pollRef = useRef(null);
  // Track latest auto signature in a ref to avoid stale closures
  const autoSigRef = useRef('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [isQrLoading, setIsQrLoading] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [qrError, setQrError] = useState('');
  const [autoSignatureUrl, setAutoSignatureUrl] = useState('');
  const [canSubmit, setCanSubmit] = useState(false);

  // Helper to set auto signature synchronously in both state & ref
  const setAutoSignature = useCallback((val) => {
    autoSigRef.current = val || '';
    setAutoSignatureUrl(val || '');
  }, []);

  // Update submit button state using refs (stable function, no deps needed)
  const updateCanSubmit = useCallback(() => {
    const hasAutoSignature = !!autoSigRef.current;
    const hasPadData = sigPadRef.current && !sigPadRef.current.isEmpty();
    const newCanSubmit = hasAutoSignature || hasPadData;
    console.log('updateCanSubmit:', { hasAutoSignature, hasPadData, newCanSubmit });
    setCanSubmit(newCanSubmit);
  }, []);

  // Re-evaluate whenever autoSignatureUrl state changes (external consumers might rely on it)
  useEffect(() => {
    updateCanSubmit();
  }, [autoSignatureUrl, updateCanSubmit]);

  // Helper to (re)size canvas pixel buffer to 18:6 based on its rendered width
  const sizeCanvasToAspect = useCallback((preserve = true) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Get CSS pixel width and compute height from 18:6
    const cssWidth = canvas.offsetWidth;
    const ASPECT_W = 18;
    const ASPECT_H = 6;
    const cssHeight = Math.round((cssWidth * ASPECT_H) / ASPECT_W);
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const data = preserve && sigPadRef.current ? sigPadRef.current.toData() : null;
    canvas.setAttribute('height', cssHeight);
    canvas.width = Math.floor(cssWidth * ratio);
    canvas.height = Math.floor(cssHeight * ratio);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

    // Reusable options for SignaturePad
    const padOptions = {
      penColor: '#1565c0',
      backgroundColor: '#fff',
      minWidth: 1.5,
      maxWidth: 3,
    };

    if (sigPadRef.current) {
      sigPadRef.current.off();
    }
    sigPadRef.current = new SignaturePad(canvas, padOptions);

    // Listen to drawing end to mark signature present
    try {
      sigPadRef.current.onEnd = () => {
        console.log('SignaturePad onEnd called');
        setTimeout(updateCanSubmit, 10); // Small delay to ensure pad state is updated
      };
    } catch (e) { /* ignore */ }
    // Prefer re-applying auto-filled image if present, otherwise restore strokes
    if (autoSigRef.current) {
      try {
        sigPadRef.current.fromDataURL(autoSigRef.current);
        // Always add a minimal stroke after drawing the backend image
        if (typeof sigPadRef.current.fromData === 'function') {
          sigPadRef.current.fromData([{ points: [{ x: 0, y: 0 }, { x: 0.1, y: 0.1 }] }]);
        }
      } catch (e) { /* ignore */ }
    } else if (data && data.length) {
      try { sigPadRef.current.fromData(data); setTimeout(updateCanSubmit, 10); } catch (e) { /* ignore restore errors */ }
    } else {
      sigPadRef.current.clear();
    }
  }, [updateCanSubmit]);

  // Draw base64 signature into pad and make it count as non-empty
  const applySignatureImage = async (dataUrl) => {
    sizeCanvasToAspect(true); // ensure pad exists & is sized
    if (!sigPadRef.current) return;
    try {
      if (typeof sigPadRef.current.fromDataURL === 'function') {
        sigPadRef.current.fromDataURL(dataUrl);
        if (typeof sigPadRef.current.fromData === 'function') {
          sigPadRef.current.fromData([{ points: [{ x: 0, y: 0 }, { x: 0.1, y: 0.1 }] }]);
        }
      } else {
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve; img.onerror = reject; img.src = dataUrl;
        });
        const c = canvasRef.current;
        const ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0, c.width / (window.devicePixelRatio || 1), c.height / (window.devicePixelRatio || 1));
        if (typeof sigPadRef.current.fromData === 'function') {
          sigPadRef.current.fromData([{ points: [{ x: 0, y: 0 }, { x: 0.1, y: 0.1 }] }]);
        }
      }
      setAutoSignature(dataUrl);
      // Immediately allow submit (no race with stale closures)
      setCanSubmit(true);
      // Fallback delayed validation
      setTimeout(updateCanSubmit, 20);
    } catch (e) {
      // ignore draw errors
    }
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // Wait until canvas is mounted and has layout (offsetWidth > 0)
  const ensureCanvasReady = (cb, tries = 12) => {
    const c = canvasRef.current;
    if (c && c.offsetWidth > 0) {
      cb();
      return;
    }
    if (tries <= 0) {
      cb();
      return;
    }
    requestAnimationFrame(() => ensureCanvasReady(cb, tries - 1));
  };

  const startPolling = (token) => {
    stopPolling();
    if (!token) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await areaApi.checkMobileSignature(token);
        const sig = data && (data.signature !== undefined ? data.signature : data?.data?.signature);
        if (sig && sig !== false) {
          const normalized = typeof sig === 'string' && sig.startsWith('data:') ? sig : `data:image/png;base64,${sig}`;
          stopPolling();
          setAutoSignature(normalized);
          setShowQr(false);
          ensureCanvasReady(() => {
            requestAnimationFrame(async () => {
              await applySignatureImage(normalized);
            });
          });
        }
      } catch (e) {
        // ignore and keep polling
      }
    }, 2000);
  };

  useEffect(() => {
    if (isOpened) {
      // Reset QR view on open
      setShowQr(false);
      setIsQrLoading(false);
      setQrUrl('');
      setQrError('');
      setAutoSignature('');
      setCanSubmit(false);
      stopPolling();
      // Initial sizing to 18:6
      requestAnimationFrame(() => sizeCanvasToAspect(false));
      // Keep in sync on window resize while open
      const onResize = () => requestAnimationFrame(() => sizeCanvasToAspect(true));
      window.addEventListener('resize', onResize);
      window.addEventListener('orientationchange', onResize);
      return () => {
        window.removeEventListener('resize', onResize);
        window.removeEventListener('orientationchange', onResize);
        stopPolling();
      };
    }
    return undefined;
  }, [isOpened, sizeCanvasToAspect, setAutoSignature]);

  useEffect(() => () => { if (sigPadRef.current) sigPadRef.current.off(); }, []);

  const handleClear = () => {
    if (isSubmitting) return;
    if (sigPadRef.current) sigPadRef.current.clear();
    setAutoSignature('');
    setTimeout(updateCanSubmit, 10);
  };

  const handleSubmit = async () => {
    console.log('handleSubmit called');
    console.log('canSubmit:', canSubmit);
    console.log('autoSignatureUrl:', !!autoSignatureUrl);
    console.log('padHasData:', sigPadRef.current && !sigPadRef.current.isEmpty());

    if (isSubmitting) return;

    setIsSubmitting(true);
    let dataUrl;
    const padHasData = sigPadRef.current && !sigPadRef.current.isEmpty();

    // Priority: use autoSignatureUrl if available, then check pad data
    if (autoSignatureUrl) {
      dataUrl = autoSignatureUrl;
      console.log('Using autoSignatureUrl');
    } else if (padHasData) {
      dataUrl = canvasRef.current.toDataURL('image/png');
      console.log('Using canvas data');
    } else {
      // Fallback: if both are missing, show error or bail out
      console.log('No signature data found');
      setIsSubmitting(false);
      return;
    }

    console.log('Submitting signature...');
    await onSubmit(dataUrl, () => setIsSubmitting(false));
  };

  const fallbackOrigin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
  const baseMobileUrl = mobileSignUrl || `${fallbackOrigin}/mobile-signature`;

  const onMobileSignClick = async () => {
    if (isSubmitting) return;
    // Require preLoanId
    if (!preLoanId) {
      setShowQr(true);
      setQrError('');
      setIsQrLoading(false);
      // Without preLoanId we cannot request a token; bail out with simple info
      setQrError('signature_modal_missing_preloan_id');
      return;
    }
    setAutoSignature('');
    setCanSubmit(false);
    setShowQr(true);
    setIsQrLoading(true);
    setQrError('');
    try {
      const { data } = await areaApi.requestMobileSignatureToken(preLoanId);
      const token = data?.token;
      if (!token) {
        setQrError('signature_modal_token_error');
        setIsQrLoading(false);
        return;
      }
      const joiner = baseMobileUrl.includes('?') ? '&' : '?';
      const urlWithToken = `${baseMobileUrl}${joiner}token=${encodeURIComponent(token)}`;
      setQrUrl(urlWithToken);
      startPolling(token);
    } catch (e) {
      setQrError('signature_modal_token_error');
    } finally {
      setIsQrLoading(false);
    }
  };

  // Stop polling when leaving QR view manually
  useEffect(() => {
    if (!showQr) {
      stopPolling();
    }
  }, [showQr]);

  return isOpened ? (
    <div className="popup-overlay" style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(47,46,65,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <SignatureModalContent>
        <button className="close-button" onClick={isSubmitting ? undefined : onClose} aria-label="Zavrieť modal" disabled={isSubmitting}>
          <svg width="28" height="28" viewBox="0 0 24 24"><path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11l4.89 4.89-4.89 4.89a1 1 0 1 0 1.41 1.41l4.89-4.89 4.89 4.89a1 1 0 0 0 1.41-1.41l-4.89-4.89 4.89-4.89a1 1 0 0 0 0-1.41z" fill="#333"/></svg>
        </button>
        <div className="signature-title">
          <FormattedMessage id="signature_modal_title"/>
        </div>
        <div className="signature-hint">
          <FormattedMessage id={showQr ? 'signature_modal_instruction_qr' : 'signature_modal_instruction'} />
        </div>
        {!showQr ? (
          <>
            <div className="canvas-wrap">
              <canvas
                ref={canvasRef}
                className="signature-pad"
              />
              <button
                type="button"
                className="clear-icon"
                onClick={handleClear}
                disabled={isSubmitting}
                aria-label="Vymazať podpis"
                title="Vymazať podpis"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 3a1 1 0 0 0-1 1v1H5a1 1 0 1 0 0 2h.3l.9 12.1A2 2 0 0 0 8.2 21h7.6a2 2 0 0 0 2-1.9L18.7 7H19a1 1 0 1 0 0-2h-3V4a1 1 0 0 0-1-1H9zm2 2h2v1h-2V5zM9.3 7h5.4l.9 12H8.4L9.3 7zM10 9a1 1 0 0 0-1 1v7a1 1 0 1 0 2 0v-7a1 1 0 0 0-1-1zm4 0a1 1 0 0 0-1 1v7a1 1 0 1 0 2 0v-7a1 1 0 0 0-1-1z"/></svg>
              </button>
            </div>
            <div className="signature-actions">
              <Button
                type="PRIMARY"
                onClick={handleSubmit}
                disabled={isSubmitting || !canSubmit}
              >
                <FormattedMessage id="signature_modal_submit" defaultMessage="Potvrdiť podpis" />
              </Button>
              <Button type="OUTLINED" onClick={onMobileSignClick} disabled={isSubmitting}><FormattedMessage id="signature_modal_sign_on_mobile" defaultMessage="Podpísať na mobilnom telefóne" /></Button>
            </div>
          </>
        ) : (
          <div className="qr-panel">
            {isQrLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 220 }}>
                <InlineLoading />
              </div>
            ) : qrError ? (
              <div style={{ color: '#c62828', fontSize: 14, marginBottom: 8, textAlign: 'center' }}>
                <FormattedMessage id={qrError} defaultMessage="Nastala chyba pri príprave mobilného podpisu." />
              </div>
            ) : (
              <>
                <QRCode value={qrUrl} size={192} qrStyle="squares" eyeRadius={4} quietZone={6} />
                <div className="qr-url">{qrUrl}</div>
              </>
            )}
          </div>
        )}
        {isSubmitting && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 12, marginBottom: 8 }}>
            <InlineLoading />
          </div>
        )}
      </SignatureModalContent>
    </div>
  ) : null;
};


export default SignatureModal;
